// src/components/ManualFinancialUpload.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, onSnapshot, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const PROCESSING_TIMEOUT_MS = 180000; // 3 minutes for OCR processing

const formatFileSize = (b) => {
  if (b == null || isNaN(b)) return '';
  if (b === 0) return '0 B';
  const k = 1024, s = ['B', 'KB', 'MB', 'GB', 'TB'];
  if (b < 0) return '?';
  const i = Math.max(0, Math.floor(Math.log(b) / Math.log(k)));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(2))} ${s[i] || 'B'}`;
};

const ManualFinancialUpload = ({ clientId, user, selectedClient, clientIsActive, onUploadComplete }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileLabel, setFileLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const fileInputRef = useRef(null);
  const [processingError, setProcessingError] = useState({ open: false, message: '' });
  
  // Refs to track timeouts for cleanup
  const processingTimeoutRef = useRef(null);
  const listenerTimeoutRef = useRef(null);

  // UPLOAD PERMISSION LOGIC - Single source of truth
  // Allow uploads if: client is active OR financials haven't been uploaded yet
  const allowUpload = clientIsActive || selectedClient?.isSetupComplete === false || selectedClient?.status === 'Pending Admin Review';

  const checkForDuplicateFile = async (fileToCheck) => {
    try {
      const q = query(
        collection(db, 'documents'),
        where('clientId', '==', clientId),
        where('name', '==', fileToCheck.name),
        where('parentId', '==', null)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const duplicates = querySnapshot.docs
          .map(docData => ({ id: docData.id, ...docData.data() }))
          .filter(docData =>
            docData.size != null &&
            fileToCheck.size != null &&
            Math.abs(docData.size - fileToCheck.size) < 100 
          );
        if (duplicates.length > 0) return duplicates;
      }
      return null;
    } catch (err) {
      console.error('Error checking for duplicates:', err);
      toast.warn('Could not verify duplicates due to an error.');
      return null;
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        toast.error(`File size exceeds limit of ${formatFileSize(MAX_FILE_SIZE)}. Please select a smaller file.`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setFile(null);
        return;
      }
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Only PDF files are allowed for financial document uploads.');
        if (fileInputRef.current) fileInputRef.current.value = '';
        setFile(null);
        return;
      }
      setFile(selectedFile);
      
      setCheckingDuplicates(true);
      try {
        const duplicates = await checkForDuplicateFile(selectedFile);
        if (duplicates && duplicates.length > 0) {
          toast.warn(
            `Note: A file with a similar name and size already exists: "${duplicates[0].label || duplicates[0].name}"`
          );
        }
      } catch (err) {
        console.error('Error during duplicate check:', err);
      } finally {
        setCheckingDuplicates(false);
      }
    }
  };

  const handleLabelChange = (e) => setFileLabel(e.target.value);

  // Cleanup helper to clear all timeouts and listeners
  const cleanupTimers = (unsubscribeFn) => {
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    if (listenerTimeoutRef.current) {
      clearTimeout(listenerTimeoutRef.current);
      listenerTimeoutRef.current = null;
    }
    if (unsubscribeFn) {
      unsubscribeFn();
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a PDF file to upload.");
      return;
    }
    if (!fileLabel.trim()) {
      toast.error("Please provide a label for the financial document.");
      return;
    }
    
    // Check upload permission
    if (!allowUpload) {
      toast.warn('Client is inactive. Uploads are disabled.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setCheckingDuplicates(true);
    
    try {
      const duplicates = await checkForDuplicateFile(file);
      if (duplicates && duplicates.length > 0) {
        const proceed = window.confirm(
          `A file with a similar name and size ("${duplicates[0].label || duplicates[0].name}") already exists. This could be a duplicate. Do you want to upload anyway?`
        );
        if (!proceed) {
          setCheckingDuplicates(false);
          setUploading(false);
          setFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        }
      }
    } catch (err) {
      // Error handled in checkForDuplicateFile
    } finally {
      setCheckingDuplicates(false);
    }

    const timestamp = new Date().getTime();
    const safeFileNameBase = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const uniqueFileName = `${timestamp}_${safeFileNameBase}`;
    const storageRefPath = `clients/${clientId}/uploads/manual_raw/${uniqueFileName}`;

    // PRE-CREATE the Firestore document BEFORE uploading to Storage
    // This ensures the Cloud Function can find it when triggered by onObjectFinalized
    let docRef;
    try {
      const metadata = {
        clientId: clientId || '?',
        userId: user?.uid || user?.id || '?',
        fcfoId: selectedClient?.fcfoId || null,
        name: file.name,
        fileName: uniqueFileName,
        label: fileLabel.trim(),
        type: 'financial',
        url: null, // Will be updated after upload completes
        originalFilePathInStorage: storageRefPath,
        size: file.size,
        contentType: file.type || 'application/pdf',
        uploadedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        parentId: null,
        versionNumber: 1,
        awaitingSanitization: true,
        rawFinancialFile: true,
        documentStatus: 'processing',
      };
      docRef = await addDoc(collection(db, 'documents'), metadata);
      console.log(`[DEBUG] Pre-created Firestore document: ${docRef.id} for path: ${storageRefPath}`);
    } catch (dbErr) {
      console.error('Failed to create document record before upload:', dbErr);
      toast.error('Failed to initialize upload. Please try again.');
      setUploading(false);
      return;
    }

    const storageRefInstance = ref(storage, storageRefPath);
    const uploadTask = uploadBytesResumable(storageRefInstance, file);

    // Set up the real-time listener for this specific document
    const unsubscribe = onSnapshot(doc(db, 'documents', docRef.id), (snapshot) => {
      const data = snapshot.data();
      
      if (data?.documentStatus === 'processing_failed') {
        // Show the error modal with the detailed message from the backend
        cleanupTimers(unsubscribe);
        setProcessingError({
          open: true,
          message: data.processingError || 'Document processing failed. The file may be an unreadable scanned image.'
        });
        setUploading(false);
      } else if (['awaiting sanitization', 'sanitized'].includes(data?.documentStatus?.toLowerCase())) {
        // Success! Cloud Function processed the document
        cleanupTimers(unsubscribe);
        
        if (onUploadComplete) {
          onUploadComplete();
        }
        // Only redirect to Account tab if client setup is incomplete AND no callback is handling it
        if (selectedClient && selectedClient.isSetupComplete === false && !onUploadComplete) {
          toast.success(`"${data.label}" is ready for the next step.`);
          setTimeout(() => {
            navigate('/', { state: { targetTab: 3 } }); // Tab 3 = Account
            toast.info('Redirecting to Account page to continue setup...');
          }, 1500);
        } else {
          toast.success(`"${data.label}" processed successfully.`);
        }
        setUploading(false);
      }
    });

    // Safety timeout for listener (5 minutes max, in case processing timeout doesn't fire)
    listenerTimeoutRef.current = setTimeout(() => {
      unsubscribe();
      listenerTimeoutRef.current = null;
    }, 300000);

    uploadTask.on(
      'state_changed',
      (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
      async (uploadError) => {
        // UPLOAD ERROR HANDLER
        console.error('Upload error:', uploadError);
        toast.error('Upload failed: ' + uploadError.code);
        
        // Clean up the pre-created document on upload failure
        try {
          await deleteDoc(docRef);
          console.log(`[DEBUG] Cleaned up Firestore document ${docRef.id} after upload failure`);
        } catch (cleanupErr) {
          console.error('Failed to clean up document after upload error:', cleanupErr);
        }
        
        // Clean up listener and timeouts
        cleanupTimers(unsubscribe);
        
        setUploading(false);
        setUploadProgress(0);
      },
      async () => {
        // UPLOAD SUCCESS HANDLER
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Update the pre-created document with the download URL
          await updateDoc(docRef, {
            url: downloadURL,
            updatedAt: serverTimestamp()
          });
          
          toast.info(`"${fileLabel.trim()}" uploaded. Now processing on server...`);
          
          // Start processing timeout - give Cloud Function up to 3 minutes for OCR
          // This starts AFTER upload completes, so user gets feedback if processing hangs
          processingTimeoutRef.current = setTimeout(() => {
            console.warn('[DEBUG] Cloud Function processing timeout reached');
            cleanupTimers(unsubscribe);
            toast.warn('Processing is taking longer than expected. Check Document History for status.');
            setUploading(false);
          }, PROCESSING_TIMEOUT_MS);
          
          setFile(null);
          setFileLabel('');
          if (fileInputRef.current) fileInputRef.current.value = '';
          
        } catch (dbErr) {
          console.error('Failed to update document with download URL:', dbErr);
          toast.error('Upload completed but failed to finalize. Please check Document History.');
          cleanupTimers(unsubscribe);
          setUploading(false);
        }
        
        // Note: Don't setUploading(false) here - wait for listener to confirm processing
        setUploadProgress(0);
      }
    );
  };

  return (
    <div className="space-y-4 p-1">
      <p className="text-sm text-gray-600 mb-3">
        Upload financial statements (PDFs only). Max size: {formatFileSize(MAX_FILE_SIZE)}.
        Uploaded documents will be processed for sanitization and then appear in the 'Document History' section.
      </p>
      <div>
        <label htmlFor="financialFileLabel" className="block text-sm font-medium text-gray-700 mb-1">
          Document Label*
        </label>
        <input
          type="text"
          id="financialFileLabel"
          value={fileLabel}
          onChange={handleLabelChange}
          placeholder="e.g., Q1 2024 P&L"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
          disabled={uploading || checkingDuplicates || !allowUpload}
        />
      </div>
      <div>
        <label htmlFor="financialFile" className="block text-sm font-medium text-gray-700 mb-1">
          Select PDF File*
        </label>
        <input
          type="file"
          id="financialFile"
          onChange={handleFileChange}
          ref={fileInputRef}
          accept="application/pdf"
          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
          disabled={uploading || checkingDuplicates || !allowUpload}
        />
        {file && (
          <p className="text-xs text-gray-500 mt-1">Selected: {file.name} ({formatFileSize(file.size)})</p>
        )}
      </div>
      
      {checkingDuplicates && (
        <p className="text-sm text-yellow-700 my-2">Checking for similar existing files...</p>
      )}

      {uploading && (
        <div className="my-2">
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-indigo-600 rounded-full"
              style={{ width: `${uploadProgress}%`, transition: 'width 0.2s ease-in-out' }}
            ></div>
          </div>
          <p className="text-sm text-indigo-700 text-center mt-1">
            {uploadProgress < 100 
              ? `${Math.round(uploadProgress)}% Uploaded` 
              : 'Processing document...'}
          </p>
        </div>
      )}

      <button
        onClick={handleUpload}
        className="w-full bg-indigo-600 text-white p-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 disabled:opacity-50 disabled:bg-gray-400"
        disabled={uploading || checkingDuplicates || !file || !fileLabel.trim() || !allowUpload}
      >
        {uploading ? 'Uploading...' : checkingDuplicates ? 'Checking...' : 'Upload Financial Document'}
      </button>
      
      <Dialog
        open={processingError.open}
        onClose={() => setProcessingError({ open: false, message: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon color="error" />
            Document Processing Issue
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {processingError.message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            After converting your document, please delete the failed upload from your Document History and upload the new searchable PDF.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setProcessingError({ open: false, message: '' })}
            variant="contained"
          >
            Understood
          </Button>
        </DialogActions>
      </Dialog>
      
      {!allowUpload && (
        <p className="text-xs text-orange-600 mt-2">Client is inactive. Uploads are disabled.</p>
      )}
    </div>
  );
};

export default ManualFinancialUpload;